// ── Enums ──────────────────────────────────────────────────────────────────────

export type ApproverType = 'Role' | 'User';
export type WorkflowApprovalStatus = 'Pending' | 'Approved' | 'Rejected' | 'Delegated' | 'Escalated';
export type OverallWorkflowStatus = 'InProgress' | 'Approved' | 'Rejected' | 'Cancelled';

// ── Workflow Level DTO (mirrors backend WorkflowLevelDto) ─────────────────────

export interface WorkflowLevelDto {
  id:                       string;
  levelNumber:              number;
  levelName:                string;
  approverType:             ApproverType;
  approverRoleId?:          string | null;
  approverRoleName?:        string | null;
  approverUserId?:          string | null;
  approverUserName?:        string | null;
  minAmount?:               number | null;
  maxAmount?:               number | null;
  currencyCode?:            string | null;
  locationId?:              string | null;
  locationName?:            string | null;
  costCenterId?:            string | null;
  costCenterName?:          string | null;
  mandatoryRemarks:         boolean;
  allowSendBack:            boolean;
  allowReject:              boolean;
  allowDelegate:            boolean;
  allowEscalation:          boolean;
  escalationHours?:         number | null;
  escalationRoleId?:        string | null;
  escalationRoleName?:      string | null;
  autoApprove:              boolean;
  autoPostAfterFinalApproval: boolean;
}

// ── Workflow Definition DTO ───────────────────────────────────────────────────

export interface WorkflowDefinitionDto {
  id:                  string;
  code:                string;
  name:                string;
  description?:        string | null;
  companyId:           string;
  module:              string;
  transactionTypeCode: string;
  effectiveFrom:       string;
  effectiveTo?:        string | null;
  isActive:            boolean;
  isDefault:           boolean;
  levels:              WorkflowLevelDto[];
  createdBy?:          string | null;
  createdDate:         string;
  modifiedBy?:         string | null;
  modifiedDate?:       string | null;
}

// ── Workflow List DTO ─────────────────────────────────────────────────────────

export interface WorkflowListDto {
  id:                  string;
  code:                string;
  name:                string;
  module:              string;
  transactionTypeCode: string;
  isActive:            boolean;
  isDefault:           boolean;
  levelCount:          number;
  effectiveFrom:       string;
  effectiveTo?:        string | null;
}

// ── Filter ─────────────────────────────────────────────────────────────────────

export interface WorkflowFilterDto {
  module?:              string;
  transactionTypeCode?: string;
  isActive?:            boolean;
  pageNumber?:          number;
  pageSize?:            number;
}

// ── Workflow Instance DTOs ─────────────────────────────────────────────────────

export interface WorkflowApprovalDto {
  id:                     string;
  instanceId:             string;
  levelNumber:            number;
  levelName:              string;
  assignedUserId?:        string | null;
  assignedUserName?:      string | null;
  status:                 WorkflowApprovalStatus;
  remarks?:               string | null;
  actionDate?:            string | null;
  delegatedToUserId?:     string | null;
  delegatedToUserName?:   string | null;
}

export interface WorkflowInstanceDto {
  id:             string;
  entityName:     string;
  entityId:       string;
  definitionId:   string;
  definitionName: string;
  currentLevel:   number;
  totalLevels:    number;
  overallStatus:  OverallWorkflowStatus;
  startedDate:    string;
  completedDate?: string | null;
  approvals:      WorkflowApprovalDto[];
}

// ── Request Payloads ───────────────────────────────────────────────────────────

export interface CreateWorkflowRequest {
  code:                string;
  name:                string;
  description?:        string | null;
  module:              string;
  transactionTypeCode: string;
  effectiveFrom?:      string | null;
  effectiveTo?:        string | null;
}

export interface UpdateWorkflowRequest {
  name:                string;
  description?:        string | null;
  effectiveFrom?:      string | null;
  effectiveTo?:        string | null;
}

export interface AddWorkflowLevelRequest {
  levelNumber:              number;
  levelName:                string;
  approverType:             ApproverType;
  approverRoleId?:          string | null;
  approverUserId?:          string | null;
  minAmount?:               number | null;
  maxAmount?:               number | null;
  currencyCode?:            string | null;
  locationId?:              string | null;
  costCenterId?:            string | null;
  mandatoryRemarks:         boolean;
  allowSendBack:            boolean;
  allowReject:              boolean;
  allowDelegate:            boolean;
  allowEscalation:          boolean;
  escalationHours?:         number | null;
  escalationRoleId?:        string | null;
  autoApprove:              boolean;
  autoPostAfterFinalApproval: boolean;
}

// ── Status helpers ─────────────────────────────────────────────────────────────

export const OVERALL_STATUS_LABELS: Record<OverallWorkflowStatus, string> = {
  InProgress: 'In Progress',
  Approved:   'Approved',
  Rejected:   'Rejected',
  Cancelled:  'Cancelled',
};

export const OVERALL_STATUS_COLORS: Record<OverallWorkflowStatus, string> = {
  InProgress: 'bg-amber-100 text-amber-700',
  Approved:   'bg-green-100 text-green-700',
  Rejected:   'bg-red-100 text-red-700',
  Cancelled:  'bg-gray-200 text-gray-500',
};

export const APPROVAL_STATUS_LABELS: Record<WorkflowApprovalStatus, string> = {
  Pending:   'Pending',
  Approved:  'Approved',
  Rejected:  'Rejected',
  Delegated: 'Delegated',
  Escalated: 'Escalated',
};

export const APPROVAL_STATUS_COLORS: Record<WorkflowApprovalStatus, string> = {
  Pending:   'bg-amber-100 text-amber-700',
  Approved:  'bg-green-100 text-green-700',
  Rejected:  'bg-red-100 text-red-700',
  Delegated: 'bg-blue-100 text-blue-700',
  Escalated: 'bg-purple-100 text-purple-700',
};
